import React, { useState, useEffect } from 'react';
import { Button } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getApiUrl } from '../../config/api';
import { 
  faPlus, 
  faFolderOpen, 
  faFolder,
  faBars,
  faTimes,
  faFilm,
  faVideo,
  faImage,
  faFileText,
  faRefresh,
  faEdit,
  faSave,
  faCheck,
  faCopy,
  faMusic,
  faArrowLeft,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ScriptGenerator from './ScriptGenerator';
import Navigation from '../Home/Navigation';
import VerticalNav from '../Home/VerticalNav';
import { ScriptToPromptModal } from './components';
import VideoFrameSelectionModal from '../Modal/VideoFrameSelectionModal';
import { useScriptToPromptNavigation } from '../../hooks/useScriptToPromptNavigation';
import { ScriptRegenerateService } from './services/scriptRegenerateService';
import styles from './ScriptLayout.module.css';
import { StepIndicatorNames } from '../../types';
import { useAuth } from '../../context/Auth/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import { ScriptRegenerationService } from './services/scriptRegenerationService';

interface ScriptData {
  id: string;
  projectId: string;
  title?: string;
  generatedScript: string;
  userSelections: {
    userIdea: string;
    attributes?: string;
  };
  scriptMetadata?: {
    scriptType?: string;
    tokenCount?: number;
    model?: string;
    generationTime?: number;
    version?: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface Project {
  id: string;
  title: string;
  type: string;
  description?: string;
  createdAt: string;
  hasScript: boolean;
  originalAudioUrl?: string;
  finalAudio?: string;
  originalVideoUrl?: string;
  generatedVideoUrl?: string;
  captionedVideoUrl?: string;
  thumbnail?: string;
  thumbnailUrl?: string;
  videoUrl?: string;
  finalVideoUrl?: string;
  hasCaptions?: boolean;
}

const generateTitleFromScript = (userIdea: string, maxLength: number = 8): string => {
  if (!userIdea) return 'Untitled Script';
  
  const words = userIdea.trim().split(/\s+/);
  const title = words.slice(0, maxLength).join(' ');
  
  return title.length > 50 ? title.substring(0, 47) + '...' : title;
};

// Parse script content into structured prompt inputs
const parseScriptForPrompts = (script: ScriptData, promptTypes: string[]) => {
  const scriptContent = script.generatedScript;
  const promptInputs: any = {};

  // Parse attributes for context
  const parseAttributes = (script: ScriptData) => {
    try {
      if (script.userSelections?.attributes && 
          typeof script.userSelections.attributes === 'string') {
        return JSON.parse(script.userSelections.attributes);
      }
      return {};
    } catch (error) {
      return {};
    }
  };

  const attributes = parseAttributes(script);
  const baseContext = {
    genre: attributes.genre || 'general',
    audience: attributes.audience || 'general audience',
    tone: attributes.tone || 'neutral',
    duration: attributes.duration || 'medium length',
    originalIdea: script.userSelections?.userIdea || ''
  };

  // Extract scenes for image prompts
  if (promptTypes.includes('image')) {
    const sceneMatches = scriptContent.match(/\[SCENE[^\]]*\]/gi) || [];
    const scenes = sceneMatches.map((match, index) => {
      const sceneDescription = match.replace(/\[SCENE:\s*|\]/gi, '').trim();
      
      // Find surrounding context (dialogue, actions)
      const sceneIndex = scriptContent.indexOf(match);
      const contextAfter = scriptContent.substring(sceneIndex, sceneIndex + 300);
      const dialogueMatch = contextAfter.match(/\w+:\s*"([^"]+)"/);
      const actionMatch = contextAfter.match(/\[ACTION[^\]]*\]/i);
      
      let enhancedPrompt = `Scene: ${sceneDescription}`;
      enhancedPrompt += `, Story: ${baseContext.originalIdea}`;
      enhancedPrompt += `, Genre: ${baseContext.genre}`;
      enhancedPrompt += `, Audience: ${baseContext.audience}`;
      enhancedPrompt += `, Mood: ${baseContext.tone}`;
      
      if (dialogueMatch) {
        enhancedPrompt += `, Context: "${dialogueMatch[1]}"`;
      }
      if (actionMatch) {
        const action = actionMatch[0].replace(/\[ACTION:\s*|\]/gi, '').trim();
        enhancedPrompt += `, Action: ${action}`;
      }
      
      return {
        id: `scene_${index + 1}`,
        title: `Image ${index + 1}: ${sceneDescription.substring(0, 30)}${sceneDescription.length > 30 ? '...' : ''}`,
        userIdea: enhancedPrompt,
        context: baseContext
      };
    });

    // If no scene tags found, create one general image prompt
    if (scenes.length === 0) {
      scenes.push({
        id: 'general_scene',
        title: 'Image 1: Main Visual Scene',
        userIdea: `Visual scene for: ${baseContext.originalIdea}, Genre: ${baseContext.genre}, Audience: ${baseContext.audience}, Style: cinematic and engaging, Mood: ${baseContext.tone}`,
        context: baseContext
      });
    }

    promptInputs.image = scenes;
  }

  // Extract music cues for music prompts
  if (promptTypes.includes('music')) {
    const musicMatches = scriptContent.match(/\[MUSIC[^\]]*\]/gi) || [];
    const musicPrompts = musicMatches.map((match, index) => {
      const musicDescription = match.replace(/\[MUSIC:\s*|\]/gi, '').trim();
      
      // Find surrounding context for better music description
      const musicIndex = scriptContent.indexOf(match);
      const contextBefore = scriptContent.substring(Math.max(0, musicIndex - 200), musicIndex);
      const contextAfter = scriptContent.substring(musicIndex, musicIndex + 200);
      const sceneMatch = contextBefore.match(/\[SCENE[^\]]*\]/i);
      const actionMatch = contextAfter.match(/\[ACTION[^\]]*\]/i);
      
      let enhancedPrompt = `Music style: ${musicDescription}`;
      enhancedPrompt += `, Story theme: ${baseContext.originalIdea}`;
      enhancedPrompt += `, Genre: ${baseContext.genre}`;
      enhancedPrompt += `, Audience: ${baseContext.audience}`;
      enhancedPrompt += `, Overall mood: ${baseContext.tone}`;
      enhancedPrompt += `, Duration: ${baseContext.duration}`;
      
      if (sceneMatch) {
        const scene = sceneMatch[0].replace(/\[SCENE:\s*|\]/gi, '').trim();
        enhancedPrompt += `, Scene context: ${scene}`;
      }
      if (actionMatch) {
        const action = actionMatch[0].replace(/\[ACTION:\s*|\]/gi, '').trim();
        enhancedPrompt += `, Action context: ${action}`;
      }
      
      return {
        id: `music_${index + 1}`,
        title: `Music ${index + 1}: ${musicDescription.substring(0, 20)}${musicDescription.length > 20 ? '...' : ''}`,
        userIdea: enhancedPrompt,
        context: baseContext
      };
    });

    // If no music tags found, create one general music prompt
    if (musicPrompts.length === 0) {
      musicPrompts.push({
        id: 'general_music',
        title: 'Music 1: Background Soundtrack',
        userIdea: `Background music for: ${baseContext.originalIdea}, Genre: ${baseContext.genre}, Mood: ${baseContext.tone}, Audience: ${baseContext.audience}, Duration: ${baseContext.duration}, Style: cinematic and engaging`,
        context: baseContext
      });
    }

    promptInputs.music = musicPrompts;
  }

  // Extract sound effects for SFX prompts
  if (promptTypes.includes('sfx')) {
    const sfxMatches = scriptContent.match(/\[SFX[^\]]*\]/gi) || [];
    const sfxPrompts = sfxMatches.map((match, index) => {
      const sfxDescription = match.replace(/\[SFX:\s*|\]/gi, '').trim();
      
      // Find surrounding context for better SFX description
      const sfxIndex = scriptContent.indexOf(match);
      const contextBefore = scriptContent.substring(Math.max(0, sfxIndex - 150), sfxIndex);
      const contextAfter = scriptContent.substring(sfxIndex, sfxIndex + 150);
      const sceneMatch = contextBefore.match(/\[SCENE[^\]]*\]/i);
      const dialogueMatch = contextAfter.match(/\w+:\s*"([^"]+)"/);
      
      let enhancedPrompt = `Sound effect: ${sfxDescription}`;
      enhancedPrompt += `, Story context: ${baseContext.originalIdea}`;
      enhancedPrompt += `, Genre: ${baseContext.genre}`;
      enhancedPrompt += `, Mood: ${baseContext.tone}`;
      enhancedPrompt += `, Quality: high-quality, clear, realistic`;
      
      if (sceneMatch) {
        const scene = sceneMatch[0].replace(/\[SCENE:\s*|\]/gi, '').trim();
        enhancedPrompt += `, Setting: ${scene}`;
      }
      if (dialogueMatch) {
        enhancedPrompt += `, During dialogue: "${dialogueMatch[1]}"`;
      }
      
      return {
        id: `sfx_${index + 1}`,
        title: `Sound Effect ${index + 1}: ${sfxDescription.substring(0, 20)}${sfxDescription.length > 20 ? '...' : ''}`,
        userIdea: enhancedPrompt,
        context: baseContext
      };
    });

    // If no SFX tags found, create one general ambience prompt
    if (sfxPrompts.length === 0) {
      sfxPrompts.push({
        id: 'general_sfx',
        title: 'Sound Effect 1: Ambient Atmosphere',
        userIdea: `Ambient sound effects for: ${baseContext.originalIdea}, Genre: ${baseContext.genre}, Mood: ${baseContext.tone}, Setting atmosphere, high-quality and immersive`,
        context: baseContext
      });
    }

    promptInputs.sfx = sfxPrompts;
  }

  // Extract narration/dialogue for narration prompts
  if (promptTypes.includes('narration')) {
    const dialogueMatches = scriptContent.match(/\w+:\s*"[^"]*"/gi) || [];
    const narrationPrompts = dialogueMatches.slice(0, 3).map((match, index) => {
      const speakerMatch = match.match(/(\w+):\s*"([^"]*)"/);
      const speaker = speakerMatch ? speakerMatch[1] : 'Narrator';
      const dialogue = speakerMatch ? speakerMatch[2] : '';
      
      // Find surrounding context
      const dialogueIndex = scriptContent.indexOf(match);
      const contextBefore = scriptContent.substring(Math.max(0, dialogueIndex - 100), dialogueIndex);
      const sceneMatch = contextBefore.match(/\[SCENE[^\]]*\]/i);
      
      let enhancedPrompt = `Voice narration for character: ${speaker}`;
      enhancedPrompt += `, Sample dialogue: "${dialogue}"`;
      enhancedPrompt += `, Story: ${baseContext.originalIdea}`;
      enhancedPrompt += `, Genre: ${baseContext.genre}`;
      enhancedPrompt += `, Audience: ${baseContext.audience}`;
      enhancedPrompt += `, Tone: ${baseContext.tone}`;
      enhancedPrompt += `, Voice style: clear, engaging, professional`;
      
      if (sceneMatch) {
        const scene = sceneMatch[0].replace(/\[SCENE:\s*|\]/gi, '').trim();
        enhancedPrompt += `, Scene context: ${scene}`;
      }
      
      return {
        id: `narration_${index + 1}`,
        title: `Narration ${index + 1}: ${speaker} Voice Style`,
        userIdea: enhancedPrompt,
        context: baseContext
      };
    });

    // If no dialogue found, create one general narration prompt using the full script
    if (narrationPrompts.length === 0) {
      // Clean the script content for narration
      const cleanScriptContent = scriptContent
        .replace(/\[SCENE:[^\]]*\]/gi, '') // Remove scene markers
        .replace(/\[ACTION:[^\]]*\]/gi, '') // Remove action markers
        .replace(/\[SFX:[^\]]*\]/gi, '') // Remove SFX markers
        .replace(/\[MUSIC:[^\]]*\]/gi, '') // Remove music markers
        .replace(/\s+/g, ' ') // Clean up whitespace
        .trim();
      
      narrationPrompts.push({
        id: 'general_narration',
        title: 'Narration 1: Full Story Narration',
        userIdea: `Full story narration: ${cleanScriptContent}`,
        context: baseContext
      });
    }

    promptInputs.narration = narrationPrompts;
  }

  return promptInputs;
};

const ScriptLayout: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  const { showNotification } = useNotification();
  const { t } = useTranslation('scriptGenerator');
  
  // Check if we're on the script-generator route
  const isScriptGeneratorRoute = location.pathname.endsWith('/script-generator');
  
  const [scriptGeneratorKey, setScriptGeneratorKey] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedScriptId, setSelectedScriptId] = useState<string | null>(null);
  const [selectedScript, setSelectedScript] = useState<ScriptData | null>(null);
  const [showScriptGenerator, setShowScriptGenerator] = useState(isScriptGeneratorRoute);
  const [scripts, setScripts] = useState<ScriptData[]>([]);
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [isVideoFrameModalOpen, setIsVideoFrameModalOpen] = useState(false);
  const [selectedVideoFile, setSelectedVideoFile] = useState<File | null>(null);
  const [editingScript, setEditingScript] = useState(false);
  const [editedScript, setEditedScript] = useState('');
  const [editingTitle, setEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [editingScriptTitle, setEditingScriptTitle] = useState<string | null>(null);
  const [editedScriptTitle, setEditedScriptTitle] = useState('');
  const [editingOriginalIdea, setEditingOriginalIdea] = useState(false);
  const [editedOriginalIdea, setEditedOriginalIdea] = useState('');
  const [deletingScriptId, setDeletingScriptId] = useState<string | null>(null);
  const [regeneratingScriptId, setRegeneratingScriptId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [scriptToDelete, setScriptToDelete] = useState<ScriptData | null>(null);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  
  // Modal state - keeping for backward compatibility with vertical nav
  const [showScriptToPromptModal, setShowScriptToPromptModal] = useState(false);

  // Hook for ALL script-to-prompt navigation (both vertical nav and button)
  const {
    showModal: showHookModal,
    setShowModal: setShowHookModal,
    handleProceedWithScript: hookHandleProceedWithScript,
    handleProceedWithoutScript: hookHandleProceedWithoutScript
  } = useScriptToPromptNavigation(projectId);

  // Wrapper functions to bridge old modal state with new hook logic
  const handleProceedWithScript = (scriptId: string, promptTypes: string[], useConsistentCharacters: boolean = false) => {
    return hookHandleProceedWithScript(scriptId, promptTypes, useConsistentCharacters, scripts);
  };

  const handleProceedWithoutScript = () => {
    return hookHandleProceedWithoutScript();
  };

  const handleCloseScriptToPromptModal = () => {
    setShowScriptToPromptModal(false);
  };

  // Handle navigation to video editor with audio URL logic
  const handleContentCreationNavigation = () => {
    if (!project || !projectId) {
      navigate('/video-editor');
      return;
    }

    // Priority order: finalAudio → originalAudioUrl → ask user to generate audio
    let audioUrl = null;
    let audioSource = '';

    if (project.finalAudio) {
      audioUrl = project.finalAudio;
      audioSource = 'final';
      console.log('📹 Using final audio URL for video editor:', audioUrl);
    } else if (project.originalAudioUrl) {
      audioUrl = project.originalAudioUrl;
      audioSource = 'original';
      console.log('📹 Using original audio URL for video editor:', audioUrl);
    }

    if (audioUrl) {
      // Navigate to video editor with audio URL
      const queryParams = new URLSearchParams({
        'audio-url': audioUrl,
        'audio-source': audioSource,
        'project-id': projectId
      });
      
      navigate(`/video-editor/${projectId}?${queryParams.toString()}`);
      
      // showNotification({
      //   title: 'Opening Video Editor',
      //   message: `Loading with ${audioSource === 'final' ? 'processed' : 'original'} audio`,
      //   type: 'info'
      // });
    } else {
      // No audio available - show modal asking user to generate audio first
      showNotification({
        title: 'Audio Required',
        message: 'Please generate audio first using the Audio Generation app, then try again.',
        type: 'warning'
      });

      // Navigate to audio generation for this project
      navigate(`/audio-editor/${projectId}`);
    }
  };

  // Handle navigation to caption creation with video URL dependency check
  const handleCaptionCreationNavigation = () => {
    if (!project || !projectId) {
      navigate('/Caption-LandingPage');
      return;
    }

    // Check for video URL first
    if (project.videoUrl || project.finalVideoUrl) {
      // Video available - proceed to caption creation
      const videoUrl = project.finalVideoUrl || project.videoUrl;
      navigate(`/caption/${projectId}?video-url=${encodeURIComponent(videoUrl)}`);
      
      // showNotification({
      //   title: 'Opening Caption Editor',
      //   message: 'Loading with project video',
      //   type: 'info'
      // });
      return;
    }

    // No video available - check for audio to guide user
    let audioUrl = null;
    let audioSource = '';

    if (project.finalAudio) {
      audioUrl = project.finalAudio;
      audioSource = 'final';
    } else if (project.originalAudioUrl) {
      audioUrl = project.originalAudioUrl;
      audioSource = 'original';
    }

    if (audioUrl) {
      // Audio available - guide user to create video first
      showNotification({
        title: 'Video Required for Captions',
        message: 'Please create a video first using Content Creation, then try again.',
        type: 'warning'
      });

      // Navigate to video editor with the available audio
      const queryParams = new URLSearchParams({
        'audio-url': audioUrl,
        'audio-source': audioSource,
        'project-id': projectId
      });
      
      navigate(`/video-editor/${projectId}?${queryParams.toString()}`);
    } else {
      // No audio available - guide user to generate audio first
      showNotification({
        title: 'Audio Required First',
        message: 'Please generate audio first, then create a video, before adding captions.',
        type: 'warning'
      });

      // Navigate to audio generation for this project
      navigate(`/audio-editor/${projectId}`);
    }
  };

  // Handle frame selection from video extractor
  const handleFrameSelected = async (imageData: string, timestamp: number) => {
    console.log('🎯 [SCRIPT_PAGE] Frame selected handler called:', { timestamp, imageDataLength: imageData.length });
    
    // Close the video frame modal first
    setIsVideoFrameModalOpen(false);
    setSelectedVideoFile(null);
    
    try {
      console.log('⏳ [SCRIPT_PAGE] Starting frame upload process...');
      
      // We need to import the uploadExtractedFrameImage function
      const { uploadExtractedFrameImage } = await import('../../services/thumbnailGenerationApi');
      
      // Upload the extracted frame image using the existing project
      const result = await uploadExtractedFrameImage(imageData, {
        projectId: projectId || '',
        title: `${project?.title || 'Project'} - Video Frame at ${Math.floor(timestamp / 60)}:${Math.floor(timestamp % 60).toString().padStart(2, '0')}`,
        timestamp: timestamp
      });
      
      console.log('📤 [SCRIPT_PAGE] Upload result:', result);
      
      if (result) {
        // showNotification({
        //   title: 'Frame Selected',
        //   message: 'Frame extracted successfully! Redirecting to thumbnail editor.',
        //   type: 'success'
        // });
        
        console.log('🚀 [SCRIPT_PAGE] Navigating to thumbnail editor...');
        // Navigate to thumbnail editor with the extracted frame
        navigate(`/thumbnail-editor/${projectId}?baseImage=${encodeURIComponent(result.s3Url)}`);
      }
    } catch (error) {
      console.error('❌ [SCRIPT_PAGE] Frame selection error:', error);
      showNotification({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to process extracted frame',
        type: 'error'
      });
    }
  };

  // Handle vertical navigation
  const handleNavSelected = (key: StepIndicatorNames) => {
    switch (key) {
      case StepIndicatorNames.IdeaCreation:
        // If we're already on the script page, show modal directly
        if (window.location.pathname.includes('/script-generator/') || window.location.pathname.includes('/script-layout/')) {
          setShowScriptToPromptModal(true);
        } else {
          // Navigate to script page and auto-open modal
          if (projectId) {
            navigate(`/script-generator/${projectId}?openPromptModal=true`);
          } else {
            navigate('/script-generator?openPromptModal=true');
          }
        }
        break;
      case StepIndicatorNames.ScriptGeneration:
        if (projectId) {
          navigate(`/script-generator/${projectId}`);
        } else {
          navigate('/script-generator');
        }
        break;
      case StepIndicatorNames.AudioGeneration:
        if (projectId) {
          navigate(`/audio-editor/${projectId}`);
        } else {
          navigate('/audio-generation');
        }
        break;
      case StepIndicatorNames.ContentCreation:
        // Enhanced logic: Check for audio URLs and pass to video editor
        if (projectId) {
          handleContentCreationNavigation();
        } else {
          navigate('/video-editor');
        }
        break;
      case StepIndicatorNames.CaptionCreation:
        // Enhanced logic: Check for video URL, fallback to audio, or guide user
        if (projectId) {
          handleCaptionCreationNavigation();
        } else {
          navigate('/Caption-LandingPage');
        }
        break;
      case StepIndicatorNames.SoundGeneration:
        if (projectId) {
          navigate(`/sound-editor/${projectId}`);
        } else {
          navigate('/sound-generation');
        }
        break;
      case StepIndicatorNames.TitleDescTag:
        if (projectId) {
          navigate(`/title-description/${projectId}`);
        } else {
          navigate('/title-landing');
        }
        break;
      case StepIndicatorNames.Export:
        if (projectId) {
          navigate(`/social-media/${projectId}`);
        } else {
          navigate('/social-media');
        }
        break;
      default:
        break;
    }
  };

  // Load project and scripts on component mount
  useEffect(() => {
    if (projectId) {
      loadProject();
      loadScripts();
    } else {
      // If no projectId, redirect to landing page
      navigate('/script-generator');
    }
  }, [projectId]);

  // Handle auto-opening prompt modal from navigation
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('openPromptModal') === 'true') {
      // Remove the query parameter and open modal
      const newUrl = location.pathname;
      window.history.replaceState({}, '', newUrl);
      setShowScriptToPromptModal(true);
    }
  }, [location.search]);

  // Handle route changes to show/hide script generator
  useEffect(() => {
    const isScriptGeneratorRoute = location.pathname.endsWith('/script-generator');
    setShowScriptGenerator(isScriptGeneratorRoute);
  }, [location.pathname]);

  const loadProject = async () => {
    if (!projectId) return;
    
    try {
      const response = await fetch(getApiUrl(`/projects/${projectId}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const result = await response.json();
      
      if (result) {
        setProject(result);
      }
    } catch (error) {
      console.error('Error loading project:', error);
    }
  };

  const loadScripts = async () => {
    if (!projectId) return;
    
    setLoading(true);
    try {
      const response = await fetch(getApiUrl(`/script-generator/scripts/${projectId}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const result = await response.json();
      
      if (result.success) {
        // Generate titles for scripts that don't have them
        const scriptsWithTitles = result.data.map((script: ScriptData) => ({
          ...script,
          title: script.title || generateTitleFromScript(script.userSelections?.userIdea || 'Untitled Script', 8)
        }));
        setScripts(scriptsWithTitles);
      }
    } catch (error) {
      console.error('Error loading scripts:', error);
      setScripts([]);
    } finally {
      setLoading(false);
    }
  };

  // Function to navigate back to project overview
  const handleNavigateToProject = () => {
    navigate(`/script-generator/${projectId}`);
    setShowScriptGenerator(false);
  };

  // Function to be called when a new script is generated
  const handleScriptCreated = async (newScriptId: string) => {
    console.log('🎯 Script created callback triggered for:', newScriptId);
    
    // Reload the scripts list to show the new script in sidebar
    await loadScripts();
    
    console.log('✅ Scripts reloaded, staying on generator to show results');
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleNewScript = () => {
    // Clear current selection and show script generator
    setShowScriptGenerator(true);
    setSelectedScriptId(null);
    setSelectedScript(null);
    
    // Force remount of ScriptGenerator by updating key
    setScriptGeneratorKey(prev => prev + 1);
    
    // Navigate to the script generator route
    navigate(`/script-generator/${projectId}/script-generator`, { replace: true });
  };

  const handleBackToProjects = () => {
    navigate('/script-generator');
  };

  const selectScript = async (scriptId: string) => {
    const script = scripts.find(s => s.id === scriptId);
    if (script) {
      setSelectedScript(script);
      setSelectedScriptId(scriptId);
      setShowScriptGenerator(false);
      // Navigate to the project overview (without /script-generator)
      navigate(`/script-generator/${projectId}`);
    }
  };

  const parseAttributes = (script: ScriptData) => {
    try {
      if (script.userSelections?.attributes && typeof script.userSelections.attributes === 'string') {
        return JSON.parse(script.userSelections.attributes);
      }
      return {};
    } catch (error) {
      console.error('Error parsing script attributes:', error);
      return {};
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const handleTitleEdit = () => {
    if (project?.title) {
      setEditedTitle(project.title);
      setEditingTitle(true);
    }
  };

  const handleTitleSave = async () => {
    if (project && editedTitle.trim() && editedTitle.trim() !== project.title) {
      try {
        const response = await fetch(getApiUrl(`/projects/${project.id}`), {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            title: editedTitle.trim()
          }),
        });

        if (response.ok) {
          // Update the local state
          setProject({
            ...project,
            title: editedTitle.trim()
          });
          setEditingTitle(false);
          setEditedTitle('');
        }
      } catch (error) {
        console.error('Failed to save title:', error);
      }
    } else {
      setEditingTitle(false);
      setEditedTitle('');
    }
  };

  const handleTitleCancel = () => {
    setEditingTitle(false);
    setEditedTitle('');
  };

  const handleTitleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleTitleSave();
    } else if (e.key === 'Escape') {
      handleTitleCancel();
    }
  };

  const handleScriptTitleEdit = (scriptId: string, currentTitle: string) => {
    setEditingScriptTitle(scriptId);
    setEditedScriptTitle(currentTitle);
  };

  const handleScriptTitleSave = async (scriptId: string) => {
    if (editedScriptTitle.trim()) {
      try {
        const response = await fetch(getApiUrl('/script-generator/update-title'), {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            scriptId: scriptId,
            title: editedScriptTitle.trim()
          }),
        });
        
        const result = await response.json();
        
        if (result.success) {
          // Update the local state
          setScripts(scripts.map(script => 
            script.id === scriptId 
              ? { ...script, title: editedScriptTitle.trim() }
              : script
          ));
          
          // If this is the selected script, update it too
          if (selectedScript && selectedScript.id === scriptId) {
            setSelectedScript({
              ...selectedScript,
              title: editedScriptTitle.trim()
            });
          }
          
          setEditingScriptTitle(null);
          setEditedScriptTitle('');
        }
      } catch (error) {
        console.error('Failed to save script title:', error);
      }
    } else {
      setEditingScriptTitle(null);
      setEditedScriptTitle('');
    }
  };

  const handleScriptTitleCancel = () => {
    setEditingScriptTitle(null);
    setEditedScriptTitle('');
  };

  const handleScriptTitleKeyPress = (e: React.KeyboardEvent, scriptId: string) => {
    if (e.key === 'Enter') {
      handleScriptTitleSave(scriptId);
    } else if (e.key === 'Escape') {
      handleScriptTitleCancel();
    }
  };

  // SEPARATE HANDLERS FOR ORIGINAL IDEA
  const handleOriginalIdeaEdit = () => {
    if (selectedScript?.userSelections?.userIdea) {
      setEditedOriginalIdea(selectedScript.userSelections.userIdea);
      setEditingOriginalIdea(true);
    }
  };

  const handleOriginalIdeaSave = async () => {
    if (selectedScript && editedOriginalIdea.trim()) {
      try {
        const response = await fetch(getApiUrl('/script-generator/update-idea'), {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            scriptId: selectedScript.id,
            userIdea: editedOriginalIdea.trim()
          }),
        });
        
        const result = await response.json();
        
        if (result.success) {
          // Update the local state
          const updatedScript = {
            ...selectedScript,
            userSelections: {
              ...selectedScript.userSelections,
              userIdea: editedOriginalIdea.trim()
            }
          };
          setSelectedScript(updatedScript);
          
          // Update in the scripts list too
          setScripts(scripts.map(script => 
            script.id === selectedScript.id ? updatedScript : script
          ));
          
          setEditingOriginalIdea(false);
          setEditedOriginalIdea('');
        }
      } catch (error) {
        console.error('Failed to save original idea:', error);
      }
    }
  };

  const handleOriginalIdeaCancel = () => {
    setEditingOriginalIdea(false);
    setEditedOriginalIdea('');
  };

  const handleScriptEdit = () => {
    if (selectedScript?.generatedScript) {
      setEditedScript(selectedScript.generatedScript);
      setEditingScript(true);
    }
  };

  const handleScriptSave = async () => {
    if (selectedScript && editedScript.trim()) {
      try {
        const response = await fetch(getApiUrl('/script-generator/update'), {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            scriptId: selectedScript.id,
            generatedScript: editedScript.trim()
          }),
        });

        if (response.ok) {
          // Update the local state
          setSelectedScript({
            ...selectedScript,
            generatedScript: editedScript.trim()
          });
          setEditingScript(false);
          setEditedScript('');
          
          // Reload scripts to update the list
          await loadScripts();
        }
      } catch (error) {
        console.error('Failed to save script:', error);
      }
    }
  };

  const handleScriptCancel = () => {
    setEditingScript(false);
    setEditedScript('');
  };

  const handleScriptRegenerate = async (script: ScriptData) => {
    if (!script) return;
    
    console.log('🔥🔥🔥 SCRIPT LAYOUT REGENERATE CLICKED 🔥🔥🔥');
    console.log('🔄 Script data:', script);
    
    setRegeneratingScriptId(script.id);
    
    try {
      // Use shared service to build payload
      const payload = ScriptRegenerateService.buildFromScriptData(script);
      
      console.log('🔄 Using shared service payload:', payload);

      // Use shared service to regenerate
      const result = await ScriptRegenerateService.regenerateScript(payload);
      
      if (result.success) {
        const newScript = ScriptRegenerateService.extractGeneratedScript(result);
        
        if (newScript) {
          // Update the script content using existing update endpoint
          const updateResponse = await fetch(getApiUrl('/script-generator/update'), {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              scriptId: script.id,
              generatedScript: newScript
            }),
          });

          if (updateResponse.ok) {
            // Update the selected script if it's the current one
            if (selectedScript && selectedScript.id === script.id) {
              setSelectedScript({
                ...selectedScript,
                generatedScript: newScript
              });
            }
            
            // Reload scripts to update the list
            await loadScripts();
            
            // showNotification({
            //   title: 'Success',
            //   message: 'Script regenerated successfully!',
            //   type: 'success'
            // });
          } else {
            throw new Error('Failed to save regenerated script');
          }
        } else {
          throw new Error('No script content received from regeneration');
        }
      } else {
        throw new Error(result.message || 'Failed to regenerate script');
      }
    } catch (error) {
      console.error('Error regenerating script:', error);
      showNotification({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to regenerate script',
        type: 'error'
      });
    } finally {
      setRegeneratingScriptId(null);
    }
  };

  // Delete script handlers
  const handleDeleteScript = (script: ScriptData, event: React.MouseEvent) => {
    event.stopPropagation();
    setScriptToDelete(script);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteScript = async () => {
    if (!scriptToDelete) return;

    setDeletingScriptId(scriptToDelete.id);
    
    try {
      const response = await fetch(getApiUrl('/script-generator/delete'), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          scriptId: scriptToDelete.id
        }),
      });

      const result = await response.json();

      if (result.success) {
        // Remove script from local state
        setScripts(scripts.filter(s => s.id !== scriptToDelete.id));
        
        // Clear selection if deleting currently selected script
        if (selectedScriptId === scriptToDelete.id) {
          setSelectedScriptId(null);
          setSelectedScript(null);
        }
        
        // Show success notification
        showNotification({
          title: 'Success',
          message: 'Script deleted successfully',
          type: 'success'
        });
        
      } else {
        throw new Error(result.message || 'Failed to delete script');
      }
    } catch (error) {
      console.error('Error deleting script:', error);
      showNotification({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to delete script',
        type: 'error'
      });
    } finally {
      setDeletingScriptId(null);
      setShowDeleteConfirm(false);
      setScriptToDelete(null);
    }
  };

  const cancelDeleteScript = () => {
    setShowDeleteConfirm(false);
    setScriptToDelete(null);
  };

  // Delete all scripts handlers
  const handleDeleteAllScripts = () => {
    if (scripts.length === 0) return;
    setShowDeleteAllConfirm(true);
  };

  const confirmDeleteAllScripts = async () => {
    if (!projectId) return;

    setDeletingAll(true);
    
    try {
      const response = await fetch(getApiUrl('/script-generator/delete-all'), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          projectId: projectId
        }),
      });

      const result = await response.json();

      if (result.success) {
        // Clear all scripts from local state
        setScripts([]);
        
        // Clear selection
        setSelectedScriptId(null);
        setSelectedScript(null);
        
        // Show success notification
        // showNotification({
        //   title: 'Success',
        //   message: result.message,
        //   type: 'success'
        // });
        
      } else {
        throw new Error(result.message || 'Failed to delete all scripts');
      }
    } catch (error) {
      console.error('Error deleting all scripts:', error);
      showNotification({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to delete all scripts',
        type: 'error'
      });
    } finally {
      setDeletingAll(false);
      setShowDeleteAllConfirm(false);
    }
  };

  const cancelDeleteAllScripts = () => {
    setShowDeleteAllConfirm(false);
  };
  return (
    <div className={styles.pageContainer}>
      <Navigation />
      <VerticalNav 
        handleNavSelected={handleNavSelected} 
        initialStep={StepIndicatorNames.ScriptGeneration}
        projectData={project}
      />
      <div className={styles.layout}>
        {/* Sidebar */}
        <div className={`${styles.sidebar} ${sidebarCollapsed ? styles.collapsed : ''}`}>
          <div className={styles.sidebarHeader}>
            <button className={styles.toggleButton} onClick={toggleSidebar}>
              <FontAwesomeIcon icon={sidebarCollapsed ? faBars : faTimes} />
            </button>
            {!sidebarCollapsed && (
              <div className={styles.headerContent}>
                <button className={styles.backButton} onClick={handleBackToProjects}>
                  <FontAwesomeIcon icon={faArrowLeft} />
                </button>
                <div className={styles.appTitleSection}>
                  <h1 className={styles.appTitle}>Script Generator</h1>
                  {project && (
                    <div className={styles.projectTitleSection}>
                      {editingTitle ? (
                        <input
                          className={styles.titleInput}
                          value={editedTitle}
                          onChange={(e) => setEditedTitle(e.target.value)}
                          onKeyDown={handleTitleKeyPress}
                          onBlur={handleTitleSave}
                          autoFocus
                        />
                      ) : (
                        <div className={styles.projectTitleWithEdit}>
                          <h2 className={styles.projectTitle}>{project.title}</h2>
                          <button
                            className={styles.editTitleBtn}
                            onClick={handleTitleEdit}
                            title="Edit title"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  {project?.description && (
                    <p className={styles.projectDescription}>{project.description}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {!sidebarCollapsed && (
            <>
              <div className={styles.actionButtons}>
                <Button
                  className={styles.newButton}
                  onClick={ ()=>{
                    if(window.innerWidth <= 768){
                        toggleSidebar();
                    }
                    handleNewScript();
                  }
                  }
                  leftSection={<FontAwesomeIcon icon={faPlus} />}
                  fullWidth
                >
                  {t('scriptGenerator.scriptLayout.newScript')}
                </Button>
                <button
                  className={styles.refreshButton}
                  onClick={ loadScripts }
                  title="Refresh scripts"
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faRefresh} className={loading ? styles.spinning : ''} />
                </button>
              </div>

              <div className={styles.scriptsHeader}>
                <h3>{t('scriptGenerator.scriptLayout.scripts')} ({scripts.length})</h3>
                {scripts.length > 0 && (
                  <button
                    className={styles.deleteAllButton}
                    onClick={handleDeleteAllScripts}
                    title="Delete all scripts"
                    disabled={deletingAll}
                  >
                    {deletingAll ? (
                      <div className={styles.spinner} />
                    ) : (
                      <FontAwesomeIcon icon={faTrash} />
                    )}
                  </button>
                )}
              </div>

              <div className={styles.projectsList}>
                {loading ? (
                  <div className={styles.loadingState}>
                    <p>{t('scriptGenerator.scriptLayout.loading')}</p>
                  </div>
                ) : scripts.length === 0 ? (
                  <div className={styles.emptyProjects}>
                    <p>{t('scriptGenerator.scriptLayout.noScripts')}</p>
                    <small>{t('scriptGenerator.scriptLayout.createFirst')}</small>
                  </div>
                ) : (
                  scripts.map(script => (
                    <div key={script.id} className={styles.projectItem}>
                      <button
                        className={`${styles.projectHeader} ${
                          selectedScriptId === script.id ? styles.selected : ''
                        }`}
                        onClick={
                          () => {
                            selectScript(script.id);

                            //Auto open on mobile
                            if(window.innerWidth <= 768){
                              toggleSidebar();
                            }
                          }
                        }
                      >
                        <FontAwesomeIcon 
                          icon={faFilm} 
                          className={styles.projectIcon}
                        />
                        <div className={styles.projectInfo}>
                          {editingScriptTitle === script.id ? (
                            <input
                              className={styles.scriptTitleInput}
                              value={editedScriptTitle}
                              onChange={(e) => setEditedScriptTitle(e.target.value)}
                              onKeyDown={(e) => handleScriptTitleKeyPress(e, script.id)}
                              onBlur={() => handleScriptTitleSave(script.id)}
                              autoFocus
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            <div className={styles.scriptTitleContainer}>
                              <span className={styles.projectName}>
                                {script.title || 'Untitled Script'}
                              </span>
                              <div className={styles.scriptActions}>
                                <div
                                  className={styles.editScriptTitleBtn}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleScriptTitleEdit(script.id, script.title || 'Untitled Script');
                                  }}
                                  title="Edit script title"
                                >
                                  <FontAwesomeIcon icon={faEdit} />
                                </div>
                                <div
                                  className={styles.deleteScriptBtn}
                                  onClick={(e) => handleDeleteScript(script, e)}
                                  title="Delete script"
                                >
                                  {deletingScriptId === script.id ? (
                                    <div className={styles.spinner} />
                                  ) : (
                                    <FontAwesomeIcon icon={faTrash} />
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                          <small className={styles.projectDate}>
                            {formatDate(script.createdAt)}
                          </small>
                          <small className={styles.scriptType}>
                            {script.scriptMetadata?.scriptType || parseAttributes(script).genre || 'Unknown Type'}
                          </small>
                        </div>
                      </button>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>

        {/* Main Content */}
        <div className={styles.mainContent}>
          {showScriptGenerator ? (
            <ScriptGenerator 
              key={scriptGeneratorKey}
              onScriptCreated={handleScriptCreated}
              onNavigateToProject={handleNavigateToProject}
            />
          ) : selectedScript ? (
            <div className={styles.scriptViewer}>
              <div className={styles.scriptHeader}>
                <h2>{t('scriptGenerator.scriptLayout.scriptDetails')}</h2>
                <small>{t('scriptGenerator.scriptLayout.created')}: {formatDate(selectedScript.createdAt)}</small>
              </div>
              
              <div className={styles.scriptContent}>
                <div className={styles.scriptSection}>
                  <div className={styles.scriptSectionHeader}>
                    <h3>{t('scriptGenerator.scriptLayout.originalIdea')}</h3>
                    <button
                      className={styles.editScriptBtn}
                      onClick={handleOriginalIdeaEdit}
                      title="Edit original idea"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                  </div>
                  {editingOriginalIdea ? (
                    <div className={styles.editingContainer}>
                      <textarea
                        className={styles.ideaEditor}
                        value={editedOriginalIdea}
                        onChange={(e) => setEditedOriginalIdea(e.target.value)}
                        rows={3}
                      />
                      <div className={styles.editingActions}>
                        <button
                          className={styles.saveButton}
                          onClick={handleOriginalIdeaSave}
                          title="Save changes"
                        >
                          <FontAwesomeIcon icon={faCheck} />
                        </button>
                        <button
                          className={styles.cancelButton}
                          onClick={handleOriginalIdeaCancel}
                          title="Cancel"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p>{selectedScript.userSelections?.userIdea || t('scriptGenerator.scriptLayout.noIdeaRecorded')}</p>
                  )}
                </div>

                <div className={styles.scriptSection}>
                  <div className={styles.scriptSectionHeader}>
                    <h3>{t('scriptGenerator.scriptLayout.scriptDetailsLabel')}</h3>
                  </div>
                  {(() => {
                    const attributes = parseAttributes(selectedScript);
                    return (
                      <div className={styles.scriptMetadata}>
                        <div className={styles.metadataItem}>
                          <span className={styles.metadataLabel}>{t('scriptGenerator.scriptLayout.type')}</span>
                          <span>{selectedScript.scriptMetadata?.scriptType || 'N/A'}</span>
                        </div>
                        <div className={styles.metadataItem}>
                          <span className={styles.metadataLabel}>{t('scriptGenerator.scriptLayout.genre')}</span>
                          <span>{attributes.genre || 'N/A'}</span>
                        </div>
                        <div className={styles.metadataItem}>
                          <span className={styles.metadataLabel}>{t('scriptGenerator.scriptLayout.duration')}</span>
                          <span>{attributes.duration || 'N/A'}</span>
                        </div>
                        <div className={styles.metadataItem}>
                          <span className={styles.metadataLabel}>{t('scriptGenerator.scriptLayout.audience')}</span>
                          <span>{attributes.audience || 'N/A'}</span>
                        </div>
                        <div className={styles.metadataItem}>
                          <span className={styles.metadataLabel}>{t('scriptGenerator.scriptLayout.tone')}</span>
                          <span>{attributes.tone || 'N/A'}</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
                
                <div className={styles.scriptSection}>
                  <div className={styles.scriptSectionHeader}>
                    <h3>{t('scriptGenerator.scriptLayout.generatedScript')}</h3>
                    <button
                      className={styles.copyButton}
                      onClick={() => {
                        navigator.clipboard.writeText(selectedScript.generatedScript);
                        showNotification({
                          title: 'Copied!',
                          message: 'Script copied to clipboard',
                          type: 'success'
                        });
                      }}
                      title="Copy script"
                    >
                      <FontAwesomeIcon icon={faCopy} />
                    </button>
                  </div>
                  
                  <div className={styles.scriptActions}>
                      {editingScript ? (
                        <>
                          <button
                            className={styles.editScriptBtn}
                            onClick={handleScriptSave}
                            title="Save changes"
                          >
                            <FontAwesomeIcon icon={faCheck} />
                          </button>
                          <button
                            className={styles.cancelScriptBtn}
                            onClick={handleScriptCancel}
                            title="Cancel"
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className={styles.editScriptBtn}
                            onClick={handleScriptEdit}
                            title="Edit script"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          
                          <button
                            className={styles.editScriptBtn}
                            onClick={() => handleScriptRegenerate(selectedScript)}
                            disabled={regeneratingScriptId === selectedScript?.id}
                            title="Regenerate script"
                          >
                            {regeneratingScriptId === selectedScript?.id ? (
                              <div className={styles.spinner} />
                            ) : (
                              <FontAwesomeIcon icon={faRefresh} />
                            )}
                          </button>
                        </>
                      )}
                      
                      <button 
                        className={styles.actionButton}
                        onClick={() => setShowHookModal(true)}
                      >
                        <FontAwesomeIcon icon={faFileText} />
                        {t('scriptGenerator.scriptLayout.loadIntoPromptGenerator')}
                      </button>
                  </div>
                  
                  {editingScript ? (
                    <textarea
                      className={styles.scriptEditor}
                      value={editedScript}
                      onChange={(e) => setEditedScript(e.target.value)}
                      rows={15}
                    />
                  ) : (
                    <div className={styles.scriptText}>
                      {selectedScript.generatedScript}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.emptyState}>
              <FontAwesomeIcon icon={faFilm} size="3x" className={styles.emptyIcon} />
              <h2>{t('scriptGenerator.scriptLayout.welcomeTo')} {project?.title || 'Your Project'}</h2>
              <p>{t('scriptGenerator.scriptLayout.welcomeMessage')}</p>
              <Button
                className={styles.getStartedButton}
                onClick={handleNewScript}
                leftSection={<FontAwesomeIcon icon={faPlus} />}
                size="lg"
              >
                {t('scriptGenerator.buttons.createFirstScript')}
              </Button>
            </div>
          )}
        </div>
      </div>
      
      {/* Delete All Confirmation Dialog */}
      {showDeleteAllConfirm && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>Delete All Scripts</h3>
              <button 
                className={styles.closeButton}
                onClick={cancelDeleteAllScripts}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <p>
                Are you sure you want to delete all{' '}
                <strong>{scripts.length} script{scripts.length !== 1 ? 's' : ''}</strong>{' '}
                in this project?
              </p>
              <p className={styles.warningText}>
                This action cannot be undone.
              </p>
            </div>
            <div className={styles.modalActions}>
              <button
                className={styles.cancelButton}
                onClick={cancelDeleteAllScripts}
                disabled={deletingAll}
              >
                Cancel
              </button>
              <button
                className={styles.deleteButton}
                onClick={confirmDeleteAllScripts}
                disabled={deletingAll}
              >
                {deletingAll ? (
                  <>
                    <div className={styles.spinner} />
                    Deleting All...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faTrash} />
                    Delete All Scripts
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Single Script Dialog */}
      {showDeleteConfirm && scriptToDelete && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>Delete Script</h3>
              <button 
                className={styles.closeButton}
                onClick={cancelDeleteScript}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <p>
                Are you sure you want to delete{' '}
                <strong>"{scriptToDelete.title || 'Untitled Script'}"</strong>?
              </p>
              <p className={styles.warningText}>
                This action cannot be undone.
              </p>
            </div>
            <div className={styles.modalActions}>
              <button
                className={styles.cancelButton}
                onClick={cancelDeleteScript}
                disabled={deletingScriptId === scriptToDelete.id}
              >
                Cancel
              </button>
              <button
                className={styles.deleteButton}
                onClick={confirmDeleteScript}
                disabled={deletingScriptId === scriptToDelete.id}
              >
                {deletingScriptId === scriptToDelete.id ? (
                  <>
                    <div className={styles.spinner} />
                    Deleting...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faTrash} />
                    Delete Script
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Script to Prompt Modal - Used by BOTH vertical nav AND button */}
      {projectId && (
        <ScriptToPromptModal
          isOpen={showScriptToPromptModal || showHookModal}
          onClose={() => {
            setShowScriptToPromptModal(false);
            setShowHookModal(false);
          }}
          projectId={projectId}
          onProceedWithScript={handleProceedWithScript}
          onProceedWithoutScript={handleProceedWithoutScript}
        />
      )}
      
      {/* Video Frame Selection Modal */}
      {projectId && (
        <VideoFrameSelectionModal
          isOpen={isVideoFrameModalOpen}
          onClose={() => {
            console.log('❌ [SCRIPT_PAGE] Video frame modal closed without selection');
            setIsVideoFrameModalOpen(false);
            setSelectedVideoFile(null);
          }}
          onFrameSelected={handleFrameSelected}
          videoFile={selectedVideoFile}
        />
      )}
    </div>
  );
};

export default ScriptLayout;